import type {
	CategorySectionMap,
	CategorySectionsRepeatable,
	RepeatableSectionEntry,
} from "@loanlink-nl/model-app";
import { computed, isRef, type MaybeRefOrGetter, toValue } from "vue";
import type { DeepPartial } from "@loanlink-nl/shared";

interface ByCustomer<T> {
	customerId: string;
	sections: T;
}

/**
 * Data access methods for inventarisation sections stored by customer.
 */
function useByCustomer<T extends object>(
	entries: MaybeRefOrGetter<ByCustomer<T>[]>,
	initSections: () => T,
) {
	/**
	 * Returns a readonly computed ref that provides reactive access
	 * to the customer entry. The returned object is the actual reactive
	 * object from the entries array, so mutations to its properties
	 * will propagate back to the source.
	 */
	function getCustomerEntry(customerId: string) {
		return computed(() => {
			const entriesValue = toValue(entries);
			const index = entriesValue.findIndex(
				(entry) => entry.customerId === customerId,
			);
			if (index === -1) {
				return undefined;
			}
			// Access by index to maintain reactivity
			return entriesValue[index];
		});
	}

	const customerHasEntry = (customerId: string) => {
		const index = toValue(entries).findIndex(
			(entry) => entry.customerId === customerId,
		);
		return index !== -1;
	};

	function addCustomerEntry(customerId: string) {
		toValue(entries).push({
			customerId,
			sections: initSections(),
		});
	}

	function removeCustomerEntry(customerId: string) {
		const index = toValue(entries).findIndex(
			(entry) => entry.customerId === customerId,
		);
		if (index === -1) {
			throw new Error("Could not remove customer entry: index out of bounds.");
		}

		if (isRef(entries)) {
			entries.value.splice(index, 1);
		} else if (typeof entries === "object") {
			entries.splice(index, 1);
		}
	}

	return {
		getCustomerEntry,
		addCustomerEntry,
		removeCustomerEntry,
		customerHasEntry,
	};
}

/**
 * Data access/manipulation methods for repeatable inventarisation sections.
 */
function useRepeatableSections<T extends CategorySectionMap>(
	sections: CategorySectionsRepeatable<T>,
) {
	function getSectionIndex(section: keyof T, id: string) {
		return sections[section].findIndex((entry) => entry.id === id);
	}

	function getSection(section: keyof T, id: string) {
		return sections[section].find((entry) => entry.id === id);
	}

	function appendSection(section: keyof T) {
		sections[section].unshift({
			id: crypto.randomUUID(),
			sectionKey: section as string,
			values: {} as DeepPartial<T[typeof section]>,
		});
	}

	function removeSection(section: keyof T, id: string) {
		const sectionIndex = getSectionIndex(section, id);
		if (sectionIndex === -1) {
			throw new Error("Could not remove section entry: index out of bounds.");
		}
		sections[section].splice(sectionIndex, 1);
	}

	function removeInvalidSectionValues(section: keyof T) {
		const isValid = (entry: RepeatableSectionEntry<any>) =>
			entry.id !== undefined && entry.sectionKey !== undefined;
		sections[section] = sections[section].filter(isValid);
	}

	function removeInvalidValues() {
		const sectionKeys = Object.keys(sections) as (keyof T)[];
		sectionKeys.forEach(removeInvalidSectionValues);
	}

	return {
		appendSection,
		removeSection,
		getSectionIndex,
		getSection,
		removeInvalidValues,
	};
}

type GetSectionsType<T extends CategorySectionMap> =
	CategorySectionsRepeatable<T>;

/**
 * Data access/manipulation methods for inventarisation category with repeatable sections stored by customer.
 */
export function useRepeatableCategory<T extends CategorySectionMap>(
	entries: MaybeRefOrGetter<ByCustomer<CategorySectionsRepeatable<T>>[]>,
	initSections: () => CategorySectionsRepeatable<T>,
	repeatableSections = true,
) {
	if (repeatableSections !== true) {
		throw new Error("Non repeatable sections not supported by this composable");
	}

	const byCustomer = useByCustomer<GetSectionsType<T>>(entries, initSections);

	/**
	 * Get local index of a specific section entry.
	 */
	const getSectionIndex = (
		customerId: string,
		section: keyof T,
		id: string,
	) => {
		const customerEntry = byCustomer.getCustomerEntry(customerId).value;
		if (!customerEntry) {
			throw new Error("Could not get section index: customer entry not found.");
		}
		const { getSectionIndex } = useRepeatableSections(customerEntry.sections);
		return getSectionIndex(section, id);
	};

	const getSection = (customerId: string, section: keyof T, id: string) => {
		const customerEntry = byCustomer.getCustomerEntry(customerId).value;
		if (!customerEntry) {
			throw new Error("Could not get section: customer entry not found.");
		}
		const { getSection } = useRepeatableSections(customerEntry.sections);
		return getSection(section, id);
	};

	/**
	 * Append a new section entry.
	 */
	const appendSection = (customerId: string, section: keyof T) => {
		const customerEntry = byCustomer.getCustomerEntry(customerId).value;
		if (!customerEntry) {
			throw new Error("Could not append section: customer entry not found.");
		}
		const { appendSection } = useRepeatableSections(customerEntry.sections);
		appendSection(section);
	};

	/**
	 * Remove a specific section entry.
	 */
	const removeSection = (customerId: string, section: keyof T, id: string) => {
		const customerEntry = byCustomer.getCustomerEntry(customerId).value;
		if (!customerEntry) {
			throw new Error("Could not remove section: customer entry not found.");
		}
		const { removeSection } = useRepeatableSections(customerEntry.sections);
		removeSection(section, id);
	};

	/**
	 * Remove invalid entries from all sections.
	 * These are entries that do not have a id and sectionkey field.
	 * Useful for avoiding bug with formkit repeaters.
	 */
	const removeInvalidValues = (customerId: string) => {
		const customerEntry = byCustomer.getCustomerEntry(customerId).value;
		if (!customerEntry) {
			throw new Error(
				"Could not remove invalid values: customer entry not found.",
			);
		}
		const { removeInvalidValues: removeInvalid } = useRepeatableSections(
			customerEntry.sections,
		);
		removeInvalid();
	};

	return {
		...byCustomer,
		getSection,
		getSectionIndex,
		appendSection,
		removeSection,
		removeInvalidValues,
	};
}
